/**
 * Single source of truth for every network the dashboard knows about.
 *
 * To add or remove a network, edit only the NETWORKS array below (and drop its logo in
 * src/assets/chainLogos/). Every other per-chain map in the app is derived from it.
 *
 * - Every network gets its name + logo displayed wherever its chain id shows up.
 * - `enabled: true` networks are also listed across the UI (filters, Registry columns, timelocks).
 * - `enabled: false` networks are legacy: they are hidden from lists, but all their fields are still
 *   used so that old environments on those chains keep working (name + logo, Safe links, crons, gauges, etc.).
 */
import { defineChain, type Chain } from 'viem'
import {
  arbitrum,
  aurora,
  avalanche,
  base,
  blast,
  bsc,
  fantom,
  gnosis,
  mainnet,
  mode,
  optimism,
  polygon,
  polygonZkEvm,
  sonic,
} from 'viem/chains'
import ethereumLogoUrl from '../assets/chainLogos/ethereum-logo.svg'
import optimismLogoUrl from '../assets/chainLogos/optimism-logo.svg'
import polygonLogoUrl from '../assets/chainLogos/polygon-logo.svg'
import gnosisLogoUrl from '../assets/chainLogos/gnosis-logo.svg'
import arbitrumLogoUrl from '../assets/chainLogos/arbitrum-logo.svg'
import bscLogoUrl from '../assets/chainLogos/bsc.svg'
import fantomLogoUrl from '../assets/chainLogos/fantom.svg'
import sonicLogoUrl from '../assets/chainLogos/sonic.svg'
import avalancheLogoUrl from '../assets/chainLogos/avalanche.svg'
import baseLogoUrl from '../assets/chainLogos/base.svg'
import auroraLogoUrl from '../assets/chainLogos/aurora.svg'
import zkevmLogoUrl from '../assets/chainLogos/polygonzkevm.png'
import robinhoodLogoUrl from '../assets/chainLogos/robinhood.svg'
import genericLogo from '../assets/chainLogos/generic.png'

const DEFAULT_SAFE_ADDRESS = '0x26A0c29261d18C49e6Cf0Cee175018274D58f90C'

export interface NativeCurrency {
  name: string
  symbol: string
  decimals: number
}

export interface NetworkConfig {
  /** EVM chain id */
  id: number
  /** Constant-style key (e.g. 'MAINNET'), also used as label in chain filters */
  key: string
  enabled: boolean
  name: string
  shortName: string
  logoUrl?: string
  explorer?: string
  nativeCurrency?: NativeCurrency
  rpc?: string
  /** viem chain used by wagmi/RainbowKit to connect wallets and sign transactions */
  viemChain?: Chain
  safe?: {
    /** Network prefix used in Safe app URLs (e.g. 'eth' in `safe=eth:0x...`) */
    prefix?: string
    /** Safe web app base URL, when the chain is not supported by app.safe.global */
    appUrl?: string
    txServiceUrl?: string
    /** Client Safe address used as permission grantee */
    clientAddress?: string
  }
  /** Relayer balance gauge range, in native token */
  relayerGauge?: { minVal: number; maxVal: number }
  /** Minimum bot balance before warning, in native token (defaults to 0.1) */
  botMinBalance?: number
  /** The Graph deployment id, used by the subgraph status page */
  subgraphDeploymentId?: string
  /** Substring used to match this chain's crons by name */
  cronName?: string
}

// Not shipped by viem yet (2.24.2); drop this once viem is upgraded and import it from 'viem/chains'
const robinhood = defineChain({
  id: 4663,
  name: 'Robinhood Chain',
  nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
  rpcUrls: { default: { http: ['https://rpc.mainnet.chain.robinhood.com'] } },
  blockExplorers: { default: { name: 'Blockscout', url: 'https://robinhoodchain.blockscout.com' } },
})

export const NETWORKS: NetworkConfig[] = [
  {
    id: 1,
    key: 'MAINNET',
    enabled: true,
    name: 'Ethereum',
    shortName: 'ETH',
    logoUrl: ethereumLogoUrl,
    explorer: 'https://etherscan.io/',
    nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
    rpc: process.env.REACT_APP_MAINNET_RPC_URL || 'https://eth.llamarpc.com',
    viemChain: mainnet,
    safe: {
      prefix: 'eth',
      txServiceUrl: 'https://safe-transaction-mainnet.safe.global/api',
      clientAddress: DEFAULT_SAFE_ADDRESS,
    },
    relayerGauge: { minVal: 0.199, maxVal: 0.796 },
    botMinBalance: 0.1,
    subgraphDeploymentId: 'QmcvwVLqhqpztw4qAt1quWJAYMHYWrf2dixLc5fzWJ16ex',
    cronName: 'ethereum',
  },
  {
    id: 10,
    key: 'OPTIMISM',
    enabled: true,
    name: 'Optimism',
    shortName: 'OPT',
    logoUrl: optimismLogoUrl,
    explorer: 'https://optimistic.etherscan.io/',
    nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
    rpc: process.env.REACT_APP_OPTIMISM_RPC_URL || 'https://endpoints.omniatech.io/v1/op/mainnet/public',
    viemChain: optimism,
    safe: {
      prefix: 'oeth',
      txServiceUrl: 'https://safe-transaction-optimism.safe.global/api',
      clientAddress: DEFAULT_SAFE_ADDRESS,
    },
    relayerGauge: { minVal: 0.000375, maxVal: 0.0015 },
    botMinBalance: 0.005,
    subgraphDeploymentId: 'QmSg2fQCQk14CjtubH8kBbVgtZe7LNJyCmyeXDcmdBSxMB',
    cronName: 'optimism',
  },
  {
    id: 137,
    key: 'POLYGON',
    enabled: true,
    name: 'Polygon',
    shortName: 'matic',
    logoUrl: polygonLogoUrl,
    explorer: 'https://polygonscan.com/',
    nativeCurrency: { name: 'POL', symbol: 'POL', decimals: 18 },
    rpc: process.env.REACT_APP_POLYGON_RPC_URL || 'https://polygon-rpc.com',
    viemChain: polygon,
    safe: {
      prefix: 'matic',
      txServiceUrl: 'https://safe-transaction-polygon.safe.global/api',
      clientAddress: DEFAULT_SAFE_ADDRESS,
    },
    relayerGauge: { minVal: 0.015, maxVal: 0.06 },
    botMinBalance: 2.0,
    subgraphDeploymentId: 'QmVgApxrquC7MCNHzJTgEf7o7S7spsr1jED9DovxDK7z3u',
    cronName: 'polygon',
  },
  {
    id: 100,
    key: 'GNOSIS',
    enabled: true,
    name: 'Gnosis',
    shortName: 'GNO',
    logoUrl: gnosisLogoUrl,
    explorer: 'https://gnosisscan.io/',
    nativeCurrency: { name: 'xDAI', symbol: 'xDAI', decimals: 18 },
    rpc: process.env.REACT_APP_GNOSIS_RPC_URL || 'https://rpc.gnosischain.com',
    viemChain: gnosis,
    safe: {
      prefix: 'gno',
      txServiceUrl: 'https://safe-transaction-gnosis-chain.safe.global/api',
      clientAddress: DEFAULT_SAFE_ADDRESS,
    },
    relayerGauge: { minVal: 0.015, maxVal: 0.06 },
    botMinBalance: 0.1,
    subgraphDeploymentId: 'QmSUDRgVzRh9HwH5bVimhx73sthdz6PziM8kVrmntRGfoY',
    cronName: 'gnosis',
  },
  {
    id: 42161,
    key: 'ARBITRUM',
    enabled: true,
    name: 'Arbitrum',
    shortName: 'ARB',
    logoUrl: arbitrumLogoUrl,
    explorer: 'https://arbiscan.io/',
    nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
    rpc: process.env.REACT_APP_ARBITRUM_RPC_URL || 'https://arb1.arbitrum.io/rpc',
    viemChain: arbitrum,
    safe: {
      prefix: 'arb1',
      txServiceUrl: 'https://safe-transaction-arbitrum.safe.global/api',
      clientAddress: DEFAULT_SAFE_ADDRESS,
    },
    relayerGauge: { minVal: 0.00625, maxVal: 0.025 },
    botMinBalance: 0.0045,
    subgraphDeploymentId: 'QmWk9wBY2N2H1vLKdCQpXbWoQxEWs4CYodFnnwLuP77HFi',
    cronName: 'arbitrum',
  },
  {
    id: 1101,
    key: 'ZKEVM',
    enabled: false,
    name: 'zkEVM',
    shortName: 'zkEVM',
    logoUrl: zkevmLogoUrl,
    explorer: 'https://zkevm.polygonscan.com/',
    nativeCurrency: { name: 'ETH', symbol: 'ETH', decimals: 18 },
    rpc: process.env.REACT_APP_ZKEVM_RPC_URL || 'https://zkevm-rpc.com',
    viemChain: polygonZkEvm,
    safe: {
      txServiceUrl: 'https://safe-transaction-zkevm.safe.global/api',
      clientAddress: DEFAULT_SAFE_ADDRESS,
    },
    relayerGauge: { minVal: 0.199, maxVal: 0.796 },
    cronName: 'zkevm',
  },
  {
    id: 56,
    key: 'BSC',
    enabled: true,
    name: 'BNB Smart Chain',
    shortName: 'BNB',
    logoUrl: bscLogoUrl,
    explorer: 'https://bscscan.com/',
    nativeCurrency: { name: 'BNB', symbol: 'BNB', decimals: 18 },
    rpc:
      process.env.REACT_APP_BNB_RPC_URL ||
      'https://rough-sleek-hill.bsc.quiknode.pro/413cc98cbc776cda8fdf1d0f47003583ff73d9bf',
    viemChain: bsc,
    safe: {
      prefix: 'bnb',
      txServiceUrl: 'https://safe-transaction-bsc.safe.global/api',
      clientAddress: DEFAULT_SAFE_ADDRESS,
    },
    relayerGauge: { minVal: 0.0375, maxVal: 0.15 },
    botMinBalance: 0.02,
    subgraphDeploymentId: 'QmY28kHoUNNu3BEbA3cpdyrdZdYiAcmrbXShXbDPDEyFTt',
    cronName: 'bnb',
  },
  {
    id: 8453,
    key: 'BASE',
    enabled: true,
    name: 'Base',
    shortName: 'BASE',
    logoUrl: baseLogoUrl,
    explorer: 'https://basescan.org/',
    nativeCurrency: { name: 'ETH', symbol: 'ETH', decimals: 18 },
    rpc: process.env.REACT_APP_BASE_RPC_URL || 'https://developer-access-mainnet.base.org',
    viemChain: base,
    safe: {
      prefix: 'base',
      txServiceUrl: 'https://safe-transaction-base.safe.global/api',
      clientAddress: DEFAULT_SAFE_ADDRESS,
    },
    relayerGauge: { minVal: 0.0003375, maxVal: 0.00135 },
    subgraphDeploymentId: 'QmYP2NBm5x37QR3owjJEefELLzVyjVdTRd7cMpiaCzQoHb',
    cronName: 'base',
  },
  {
    id: 43114,
    key: 'AVALANCHE',
    enabled: true,
    name: 'Avalanche',
    shortName: 'AVAX',
    logoUrl: avalancheLogoUrl,
    explorer: 'https://snowtrace.io/',
    nativeCurrency: { name: 'Avax', symbol: 'AVAX', decimals: 18 },
    rpc: process.env.REACT_APP_AVALANCHE_RPC_URL || 'https://rpc.ankr.com/avalanche',
    viemChain: avalanche,
    safe: {
      prefix: 'avax',
      txServiceUrl: 'https://safe-transaction-avalanche.safe.global/api',
      clientAddress: DEFAULT_SAFE_ADDRESS,
    },
    relayerGauge: { minVal: 0.37, maxVal: 1.48 },
    botMinBalance: 0.1,
    subgraphDeploymentId: 'QmR43yHfTCTm1ZYQUqAJSJxkdnxSwuwB2YqqLNFA29Gr1M',
    cronName: 'avalanche',
  },
  {
    id: 250,
    key: 'FANTOM',
    enabled: false,
    name: 'Fantom',
    shortName: 'FTM',
    logoUrl: fantomLogoUrl,
    explorer: 'https://ftmscan.com/',
    nativeCurrency: { name: 'FTM', symbol: 'FTM', decimals: 18 },
    rpc: process.env.REACT_APP_FANTOM_RPC_URL || 'https://rpc.ankr.com/fantom',
    viemChain: fantom,
    safe: {
      prefix: 'ftm',
      appUrl: 'https://safe.fantom.network',
      clientAddress: DEFAULT_SAFE_ADDRESS,
    },
    relayerGauge: { minVal: 1.975, maxVal: 7.9 },
    botMinBalance: 0.1,
    subgraphDeploymentId: 'QmT1e1zPf7FNEBAiGHh8deSbHh6vLogqYPMB1bmpxZcLGu',
    cronName: 'fantom',
  },
  {
    id: 146,
    key: 'SONIC',
    enabled: true,
    name: 'Sonic',
    shortName: 'S',
    logoUrl: sonicLogoUrl,
    explorer: 'https://sonicscan.org/',
    nativeCurrency: { name: 'S', symbol: 'S', decimals: 18 },
    rpc: process.env.REACT_APP_SONIC_RPC_URL || 'https://rpc.soniclabs.com',
    viemChain: sonic,
    safe: {
      prefix: 'sonic',
      txServiceUrl: 'https://safe-transaction-sonic.safe.global/api',
      clientAddress: '0xA010C13d4a8ddd34E342EFa2b6e9b538b956AC72',
    },
    relayerGauge: { minVal: 1.975, maxVal: 7.9 },
    subgraphDeploymentId: 'QmY5em8LpSfqLdK2GqqueakPwdVanCTSbcxZtRBq3yDDMg',
  },
  {
    id: 81457,
    key: 'BLAST',
    enabled: false,
    name: 'Blast',
    shortName: 'blast',
    logoUrl: genericLogo,
    explorer: 'https://blastscan.io/',
    nativeCurrency: { name: 'ETH', symbol: 'ETH', decimals: 18 },
    rpc: process.env.REACT_APP_BLAST_RPC_URL || 'https://rpc.blast.io',
    viemChain: blast,
    safe: {
      prefix: 'blast',
      txServiceUrl: 'https://safe-transaction-blast.safe.global/api',
    },
    relayerGauge: { minVal: 0.0003375, maxVal: 0.00135 },
    botMinBalance: 0.005,
  },

  {
    id: 4663,
    key: 'ROBINHOOD',
    enabled: true,
    name: 'Robinhood Chain',
    shortName: 'RH',
    logoUrl: robinhoodLogoUrl,
    explorer: 'https://robinhoodchain.blockscout.com/',
    nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
    rpc: process.env.REACT_APP_ROBINHOOD_RPC_URL || 'https://rpc.mainnet.chain.robinhood.com',
    viemChain: robinhood,
    safe: {
      prefix: 'robinhood',
      txServiceUrl: 'https://api.safe.global/tx-service/robinhood/api',
      clientAddress: DEFAULT_SAFE_ADDRESS,
    },
    relayerGauge: { minVal: 0.00625, maxVal: 0.025 },
    botMinBalance: 0.0045,
    cronName: 'robinhood',
  },

  // Legacy networks: hidden from the UI, kept for old environments
  {
    id: 34443,
    key: 'MODE',
    enabled: false,
    name: 'Mode',
    shortName: 'mode',
    logoUrl: genericLogo,
    explorer: 'https://modescan.io/',
    nativeCurrency: { name: 'ETH', symbol: 'ETH', decimals: 18 },
    rpc: 'https://mainnet.mode.network',
    viemChain: mode,
    safe: {
      prefix: 'mode',
      appUrl: 'https://safe.optimism.io',
      txServiceUrl: 'https://safe-transaction-mode.safe.global/api',
      clientAddress: '0x8394207C31ca384232241Af2Eb0B525E55b5131E',
    },
    relayerGauge: { minVal: 0.0003375, maxVal: 0.00135 },
    botMinBalance: 0.005,
    cronName: 'mode',
  },
  {
    id: 1313161554,
    key: 'AURORA',
    enabled: false,
    name: 'Aurora',
    shortName: 'Aurora',
    logoUrl: auroraLogoUrl,
    explorer: 'https://explorer.aurora.dev/',
    nativeCurrency: { name: 'ETH', symbol: 'ETH', decimals: 18 },
    rpc: 'https://mainnet.aurora.dev',
    viemChain: aurora,
    safe: {
      clientAddress: '0xf08C4928C2615a264e7Ccf1Cfcc134920B6A9A6d',
    },
    relayerGauge: { minVal: 0.199, maxVal: 0.796 },
    subgraphDeploymentId: 'QmTpWyrXp9q9xPnLZfYwoNGVNUVUTK8NfZ4j4ZECgfmGHi',
    cronName: 'aurora',
  },
  {
    id: 324,
    key: 'ZKSYNC',
    enabled: false,
    name: 'zkSync Era',
    shortName: 'zkSync',
    safe: {
      txServiceUrl: 'https://safe-transaction-zksync.safe.global/api',
      clientAddress: '0xC78Af275e5f5B002C2CeBFd4389dACfA8a34E947',
    },
    cronName: 'zksync',
  },
  {
    id: 59144,
    key: 'LINEA',
    enabled: false,
    name: 'Linea',
    shortName: 'Linea',
    safe: {
      txServiceUrl: 'https://safe-transaction-linea.safe.global/api',
      clientAddress: '0xA010C13d4a8ddd34E342EFa2b6e9b538b956AC72',
    },
  },
  {
    id: 5,
    key: 'GOERLI',
    enabled: false,
    name: 'Goerli',
    shortName: 'görETH',
    botMinBalance: 0.000001,
    cronName: 'goerli',
  },
  {
    id: 80001,
    key: 'MUMBAI',
    enabled: false,
    name: 'Mumbai',
    shortName: 'mumbai',
    cronName: 'mumbai',
  },
]

// ---------------------------------------------------------------------------
// Derived maps — do not edit by hand, change NETWORKS instead
// ---------------------------------------------------------------------------

function mapNetworks<T>(pick: (network: NetworkConfig) => T | undefined): Record<number, T> {
  const result: Record<number, T> = {}
  NETWORKS.forEach((network) => {
    const value = pick(network)
    if (value !== undefined) result[network.id] = value
  })
  return result
}

export const ENABLED_NETWORKS = NETWORKS.filter((network) => network.enabled)

export const SupportedChainId: Record<string, number> = Object.fromEntries(
  ENABLED_NETWORKS.map((network) => [network.key, network.id])
)

export type SupportedChainIdValue = number

export const DEFAULT_CHAIN_ID = 1

export interface ChainInfoType {
  value: number
  name: string
  shortName: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  logoUrl: any
  explorer?: string
  nativeCurrency?: NativeCurrency
  rpc?: string
}

/** Display info for every known network, enabled or not, so old environments still render name + logo */
export const CHAIN_INFO: Record<number, ChainInfoType> = Object.fromEntries(
  NETWORKS.map((network) => [
    network.id,
    {
      value: network.id,
      name: network.name,
      shortName: network.shortName,
      logoUrl: network.logoUrl ?? genericLogo,
      explorer: network.explorer,
      nativeCurrency: network.nativeCurrency,
      rpc: network.rpc,
    },
  ])
)

/** Chains registered in wagmi/RainbowKit (first one is the default) */
export const WALLET_CHAINS = NETWORKS.filter((network) => network.viemChain).map((network) => network.viemChain) as [
  Chain,
  ...Chain[],
]

export const SAFE_NETWORKS = mapNetworks((network) => network.safe)
export const CLIENT_SAFE = mapNetworks((network) => network.safe?.clientAddress)
export const SAFE_TX_SERVICE_URLS = mapNetworks((network) => network.safe?.txServiceUrl)
export const RELAYER_GAUGES = mapNetworks((network) => network.relayerGauge)
export const BOT_MIN_BALANCES = mapNetworks((network) => network.botMinBalance)
export const SUBGRAPH_DEPLOYMENT_IDS = mapNetworks((network) => network.subgraphDeploymentId)
export const CRON_NAMES = mapNetworks((network) => network.cronName)
